import { useEffect } from "react";
import { Link } from "react-router-dom";
import { FileText } from "lucide-react";

export default function TermsOfService() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen pt-20" data-testid="terms-of-service-page">
      <section className="py-16 md:py-24 bg-gradient-to-b from-casino-black to-casino-dark">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-6">
            <FileText className="w-8 h-8 text-gold" />
            <h1 className="font-heading text-4xl md:text-5xl text-white">Terms of Service</h1>
          </div>
          <p className="text-white/50 text-sm">Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
        </div>
      </section>

      <section className="py-16 bg-casino-black">
        <div className="max-w-4xl mx-auto px-4 space-y-10">
          {/* Agreement */}
          <div className="prose-section">
            <h2 className="font-heading text-2xl text-white mb-4">Agreement to Terms</h2>
            <p className="text-white/70 leading-relaxed">
              By accessing and using the San Marcos Social Club website and services, you agree to be bound by these Terms of Service. If you do not agree with any part of these terms, you should not use our website or services.
            </p>
          </div>

          {/* Description of Services */}
          <div className="prose-section">
            <h2 className="font-heading text-2xl text-white mb-4">Description of Services</h2>
            <p className="text-white/70 leading-relaxed">
              San Marcos Social Club operates a social poker room in San Marcos, Texas. Our website provides information about our club, events, tournaments, membership options, and allows visitors to contact us, view our gallery, and access live stream content when available.
            </p>
          </div>

          {/* Membership */}
          <div className="prose-section">
            <h2 className="font-heading text-2xl text-white mb-4">Membership</h2>
            <p className="text-white/70 leading-relaxed">
              Membership at San Marcos Social Club is subject to applicable local, state, and federal laws. Members must be of legal age to participate in poker and gaming activities. Membership terms, fees, and benefits are as described on our membership page and may be updated from time to time.
            </p>
          </div>

          {/* User Conduct */}
          <div className="prose-section">
            <h2 className="font-heading text-2xl text-white mb-4">User Conduct</h2>
            <p className="text-white/70 leading-relaxed mb-4">
              When using our website and services, you agree not to:
            </p>
            <ul className="space-y-2 text-white/70">
              <li className="flex items-start gap-2">
                <span className="text-gold mt-1.5 text-xs">&#9679;</span>
                <span>Submit false or misleading information through any form on our website</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gold mt-1.5 text-xs">&#9679;</span>
                <span>Attempt to gain unauthorized access to any part of our website or systems</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gold mt-1.5 text-xs">&#9679;</span>
                <span>Use the website in any way that could damage, disable, or impair the site</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gold mt-1.5 text-xs">&#9679;</span>
                <span>Use automated systems or software to extract data from the website</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gold mt-1.5 text-xs">&#9679;</span>
                <span>Violate any applicable local, state, national, or international law</span>
              </li>
            </ul>
          </div>

          {/* Communications / SMS */}
          <div className="prose-section glass-card p-8 border-gold/20" data-testid="sms-terms-section">
            <h2 className="font-heading text-2xl text-gold mb-4">Communications & Text Messages</h2>
            <p className="text-white/70 leading-relaxed mb-4">
              By providing your phone number and opting in to receive text messages from San Marcos Social Club, you consent to receive SMS messages related to:
            </p>
            <ul className="space-y-2 text-white/70 mb-4">
              <li className="flex items-start gap-2">
                <span className="text-gold mt-1.5 text-xs">&#9679;</span>
                <span>Appointment confirmations and reminders</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gold mt-1.5 text-xs">&#9679;</span>
                <span>Service updates</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gold mt-1.5 text-xs">&#9679;</span>
                <span>Follow-up communications</span>
              </li>
            </ul>
            <p className="text-white/70 leading-relaxed mb-2">
              Message frequency may vary. Message and data rates may apply. You may opt out at any time by replying <strong className="text-white">STOP</strong> to any text message. Reply <strong className="text-white">HELP</strong> for assistance.
            </p>
            <p className="text-white/70 leading-relaxed">
              SMS consent is not a condition of purchase or use of any service provided by San Marcos Social Club. Your phone number and SMS consent will never be sold or shared with third parties for marketing purposes.
            </p>
          </div>

          {/* Intellectual Property */}
          <div className="prose-section">
            <h2 className="font-heading text-2xl text-white mb-4">Intellectual Property</h2>
            <p className="text-white/70 leading-relaxed">
              All content on the San Marcos Social Club website, including text, graphics, logos, images, and software, is the property of San Marcos Social Club and is protected by applicable intellectual property laws. You may not reproduce, distribute, or create derivative works from our content without written permission.
            </p>
          </div>

          {/* Disclaimer */}
          <div className="prose-section">
            <h2 className="font-heading text-2xl text-white mb-4">Disclaimer of Warranties</h2>
            <p className="text-white/70 leading-relaxed">
              Our website and services are provided on an "as is" and "as available" basis. San Marcos Social Club makes no warranties, express or implied, regarding the operation of the website or the accuracy of the information, content, or materials included on the site.
            </p>
          </div>

          {/* Limitation of Liability */}
          <div className="prose-section">
            <h2 className="font-heading text-2xl text-white mb-4">Limitation of Liability</h2>
            <p className="text-white/70 leading-relaxed">
              San Marcos Social Club shall not be liable for any damages arising from the use or inability to use our website or services. This includes but is not limited to direct, indirect, incidental, punitive, and consequential damages.
            </p>
          </div>

          {/* Changes to Terms */}
          <div className="prose-section">
            <h2 className="font-heading text-2xl text-white mb-4">Changes to Terms</h2>
            <p className="text-white/70 leading-relaxed">
              We reserve the right to modify these Terms of Service at any time. Changes will be effective when posted on our website. Your continued use of the website after changes are posted constitutes your acceptance of the modified terms.
            </p>
          </div>

          {/* Governing Law */}
          <div className="prose-section">
            <h2 className="font-heading text-2xl text-white mb-4">Governing Law</h2>
            <p className="text-white/70 leading-relaxed">
              These Terms of Service shall be governed by and construed in accordance with the laws of the State of Texas, without regard to its conflict of law provisions.
            </p>
          </div>

          {/* Contact */}
          <div className="prose-section">
            <h2 className="font-heading text-2xl text-white mb-4">Contact Us</h2>
            <p className="text-white/70 leading-relaxed">
              If you have questions about these Terms of Service, please contact us at:
            </p>
            <div className="mt-4 text-white/70">
              <p className="font-medium text-white">San Marcos Social Club</p>
              <span className="text-white/40 italic">
                Address, phone &amp; email coming soon
              </span>
            </div>
          </div>

          <div className="pt-8 border-t border-white/10">
            <Link to="/contact" className="text-gold hover:text-gold-light transition-colors text-sm">
              &larr; Back to Contact
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
